import { Currency, Dtos } from "@inrev/common";
import { Option } from "@inrev/core";
import { useAdminGetCarrierPreviews } from "@portal/api/account/admin";
import {
	useAdminFetchAgencyAccountPreviews,
	useAdminFetchAgencyPreviews,
} from "@portal/api/agency/admin";
import { useImportBond } from "@portal/api/bond/admin";
import { useFetchBondPreviews } from "@portal/api/previews";
import { FormFieldGroup } from "@portal/components/form";
import { FormDropdownComponent } from "@portal/components/form/fieldLayouts";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Button } from "@portal/components/ui/Button";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { match } from "ts-pattern";
import { z } from "zod";
import { stripEmptyResolver, typedZodResolver } from "../../../utils/form";
import { CONTRACT_SURETY_TYPE_OPTIONS } from "./constants";
import { AmountsAndPremiumSection } from "./sections/AmountsAndPremiumSection";
import { BondDetailsSection } from "./sections/BondDetailsSection";
import { BondFormSection } from "./sections/BondFormSection";
import { ProjectSection } from "./sections/ProjectSection";
import type { AdminImportBondViewFormData, DropdownOption } from "./types";

const getDefaultFormData = (): AdminImportBondViewFormData => ({
	suretyType: "contract",
	contractSuretyType: "",
	agencyId: "",
	agentId: "",
	rate: undefined,
	premium: undefined,
	agencyCommissionPercentage: undefined,
	bond: {
		status: "open",
		number: "",
		bondAmount: undefined,
		carrierName: "",
		suretyAccountId: "",
		closedAt: "",
		continuous: true,
		cancellable: false,
		notes: [],
		sfaaCodeId: null,
		companyContactId: "",
		bondCreatedFromId: "",
		data: {
			closedReason: "",
			contractAmount: undefined,
			contractDate: "",
			expectedCompletionDate: "",
			actualCompletionDate: "",
			actualContractAmount: undefined,
			bidAmount: undefined,
			bidDate: "",
			finalBondFormType: "",
			finalBondContingentRate: undefined,
			finalBondContingentPremium: undefined,
			tListingRequired: "",
			isFinalBondCompletionBond: false,
			project: {
				name: "",
				description: "",
				address: {
					city: "",
					state: "",
				},
				scopeOfWork: [],
				monthsToComplete: 12,
				contractWarrantyMonths: 12,
				contractHazards: [],
				contractDamages: [],
			},
		},
		obligees: [
			{
				name: "",
				address: {
					street: "",
					city: "",
					state: "",
					zip: "",
				},
				role: "",
			},
		],
	},
	bondForm: {
		issuanceMethod: "",
		effectiveDate: "",
		data: {
			bondFormType: "",
			contractDescription: "",
			bidBondAmountType: "",
			bondAmountPercentOfBid: undefined,
		},
	},
	issuedBondFormFile: undefined,
});

// PDF is uploaded separately as multipart, so compose the file requirement onto the JSON DTO schema.
const importBondFormSchema = Dtos.Admin.SuretyBond.Import.Request.schema.and(
	z.object({
		issuedBondFormFile: z.instanceof(File, { error: "Required" }),
	}),
);
type ImportBondFormValues = z.infer<typeof importBondFormSchema>;

export const AdminImportBondView = () => {
	const navigate = useNavigate();
	const { bondPreviews } = useFetchBondPreviews();

	const { importBond, importBondLoading, importedBondId } = useImportBond();
	const formMethods = useForm<AdminImportBondViewFormData, any, ImportBondFormValues>({
		defaultValues: getDefaultFormData(),
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(
			typedZodResolver<AdminImportBondViewFormData, any, ImportBondFormValues>(
				importBondFormSchema,
			),
		),
	});

	const contractSuretyType = formMethods.watch("contractSuretyType");
	const agencyId = formMethods.watch("agencyId");
	const suretyAccountId = formMethods.watch("bond.suretyAccountId");
	const principalCompanyContactId = formMethods.watch("bond.companyContactId");
	const bidBondId = formMethods.watch("bond.bondCreatedFromId");
	const filteredBondPreviews = useMemo(() => {
		if (contractSuretyType === "bid_to_final" && suretyAccountId !== "" && bondPreviews) {
			return bondPreviews.filter(
				(bond) =>
					bond.principalCompanyContactId === principalCompanyContactId && bond.type === "bid",
			);
		}
	}, [contractSuretyType, suretyAccountId, principalCompanyContactId, bondPreviews]);
	const bidBondPreview = useMemo(() => {
		if (bidBondId === "") return undefined;
		return filteredBondPreviews?.find((preview) => preview.id === bidBondId);
	}, [filteredBondPreviews, bidBondId]);
	const status = formMethods.watch("bond.status");
	const contractAmount = formMethods.watch("bond.data.contractAmount");
	const bidAmount = formMethods.watch("bond.data.bidAmount");
	const rate = formMethods.watch("rate");
	const finalBondContingentRate = formMethods.watch("bond.data.finalBondContingentRate");
	const premium = formMethods.watch("premium");
	const finalBondContingentPremium = formMethods.watch("bond.data.finalBondContingentPremium");

	const { agencyPreviews, agencyPreviewsLoading } = useAdminFetchAgencyPreviews();
	const { agencyAccountPreviews, agencyAccountPreviewsLoading } =
		useAdminFetchAgencyAccountPreviews(agencyId === "" ? undefined : agencyId);
	const { allCarriers, allCarriersIsLoading } = useAdminGetCarrierPreviews();

	const suretyAccount = useMemo(() => {
		if (!agencyAccountPreviews) return undefined;
		return agencyAccountPreviews.find((account) => account.id === suretyAccountId);
	}, [agencyAccountPreviews, suretyAccountId]);

	const carrierOptions = useMemo(() => {
		if (allCarriersIsLoading || !allCarriers) {
			return [];
		}

		return allCarriers.map((carrier) => ({
			label: carrier.displayName,
			value: carrier.name,
		}));
	}, [allCarriers, allCarriersIsLoading]);

	useEffect(() => {
		if (importedBondId) {
			navigate(`/bonds/${importedBondId}`);
		}
	}, [importedBondId]);

	useEffect(() => {
		formMethods.setValue("agentId", "", { shouldDirty: true });
		formMethods.setValue("bond.suretyAccountId", "", { shouldDirty: true });
	}, [agencyId]);

	useEffect(() => {
		formMethods.setValue("bond.companyContactId", "", { shouldDirty: true });
	}, [suretyAccountId]);

	useEffect(() => {
		formMethods.setValue("bond.bondCreatedFromId", "", { shouldDirty: true });
	}, [principalCompanyContactId]);

	useEffect(() => {
		if (Option.isSome(rate)) {
			if (Option.isSome(contractAmount)) {
				formMethods.setValue("premium", Currency.round(rate * contractAmount), {
					shouldDirty: true,
				});
			} else if (Option.isSome(premium)) {
				formMethods.setValue("premium", undefined, { shouldDirty: true });
			}
		}
	}, [rate, contractAmount]);

	useEffect(() => {
		if (Option.isSome(finalBondContingentRate)) {
			if (Option.isSome(bidAmount)) {
				formMethods.setValue(
					"bond.data.finalBondContingentPremium",
					Currency.round(finalBondContingentRate * bidAmount),
					{ shouldDirty: true },
				);
			} else if (Option.isSome(finalBondContingentPremium)) {
				formMethods.setValue("bond.data.finalBondContingentPremium", undefined, {
					shouldDirty: true,
				});
			}
		}
	}, [finalBondContingentRate, bidAmount]);

	useEffect(() => {
		const setValue = formMethods.setValue;
		match(contractSuretyType)
			.with("bid", () => {
				setValue("rate", undefined, { shouldDirty: true });
				setValue("premium", undefined, { shouldDirty: true });
				setValue("bond.data.finalBondContingentRate", undefined, { shouldDirty: true });
				setValue("bond.data.finalBondContingentPremium", undefined, { shouldDirty: true });
				setValue("bondForm.data.bondFormType", "bid", { shouldDirty: true });
			})
			.with("bid_to_final", "", () => {
				setValue("rate", undefined, { shouldDirty: true });
				setValue("premium", undefined, { shouldDirty: true });
				setValue("bondForm.data.bondFormType", "", { shouldDirty: true });
			})
			.with("final", () => {
				setValue("bondForm.data.bondFormType", "", { shouldDirty: true });
			})
			.exhaustive();
	}, [contractSuretyType]);

	useEffect(() => {
		if (bidBondPreview) {
			formMethods.setValue("bond.carrierName", bidBondPreview.carrierName, { shouldDirty: true });
		}
	}, [bidBondPreview]);

	const handleSubmit = ({ issuedBondFormFile, ...data }: ImportBondFormValues) => {
		importBond({ data, file: issuedBondFormFile });
	};

	const submit = () => {
		formMethods.handleSubmit(handleSubmit)();
	};

	const isBid = contractSuretyType === "bid";
	const isClosed = status === "closed";

	const accountOptions = useMemo<DropdownOption[]>(
		() =>
			agencyAccountPreviews?.map((account) => ({
				label: account.displayName ?? "??",
				value: account.id,
			})) ?? [],
		[agencyAccountPreviews],
	);
	const principalOptions = useMemo<DropdownOption[]>(
		() =>
			suretyAccount?.companies.map((company) => ({ label: company.name, value: company.id })) ?? [],
		[suretyAccount],
	);
	const bidBondOptions = useMemo<DropdownOption[]>(
		() => filteredBondPreviews?.map((bond) => ({ label: bond.number, value: bond.id })) ?? [],
		[filteredBondPreviews],
	);
	const bidBondPlaceholder =
		principalCompanyContactId === ""
			? "Select a principal before selecting a bid bond"
			: !filteredBondPreviews || filteredBondPreviews.length === 0
				? "0 Bid Bonds"
				: "Select one";

	if (agencyPreviewsLoading || !agencyPreviews) return <LoadingModal />;
	return (
		<div className="flex justify-center w-full h-full overflow-y-auto pb-[75px]">
			<div className="flex flex-col gap-y-[25px] w-[900px] max-w-[900px] mt-[50px] mx-[40px] shrink-0 h-fit">
				<div className="flex justify-between items-center gap-x-[20px]">
					<span className="text-[26px] font-bold text-primary">Import Bond</span>
					<span
						className="text-[12px] text-tertiary cursor-pointer hover:underline"
						onClick={() => {
							formMethods.reset(getDefaultFormData());
						}}
					>
						Clear
					</span>
				</div>
				<FormProvider {...formMethods}>
					<form className="flex flex-col gap-[45px]">
						<FormFieldGroup condensed>
							<FormDropdownComponent
								formMethods={formMethods}
								name="contractSuretyType"
								label="Type"
								inline
								options={CONTRACT_SURETY_TYPE_OPTIONS}
								placeholder="Select one"
							/>
						</FormFieldGroup>
						{contractSuretyType !== "" && (
							<>
								<BondDetailsSection
									contractSuretyType={contractSuretyType}
									isBid={isBid}
									isClosed={isClosed}
									accountOptions={accountOptions}
									accountReadonly={agencyAccountPreviewsLoading || !agencyAccountPreviews}
									principalOptions={principalOptions}
									carrierOptions={carrierOptions}
									bidBondOptions={bidBondOptions}
									bidBondPlaceholder={bidBondPlaceholder}
								/>
								<AmountsAndPremiumSection isBid={isBid} isClosed={isClosed} />
								<BondFormSection isBid={isBid} />
								<ProjectSection
									isBid={isBid}
									isClosed={isClosed}
									contractSuretyType={contractSuretyType}
								/>
							</>
						)}
						<div className="flex justify-end">
							<Button color="primary" filled onClick={submit} disabled={contractSuretyType === ""}>
								Import Bond
							</Button>
						</div>
					</form>
				</FormProvider>
			</div>
			{importBondLoading && <LoadingModal />}
		</div>
	);
};
